/*
 Utility to easily create and manage hotkeys across the app.
 - Supports combos like: 'Ctrl+Z', 'Ctrl+Shift+Z', 'Alt+Enter', 'Mod+Z' (Cmd on Mac, Ctrl on others)
 - Works with keydown/keyup/keypress (defaults to keydown)
 - Allows enabling/disabling and cleanup via disposer
 - Optional scoping: trigger handlers only when current scope matches

 USAGE EXAMPLES

 Basic:
   import { createHotkeys } from 'shared/utils';

   const hotkeys = createHotkeys([
     { combo: 'Mod+Z', preventDefault: true, handler: () => undo() },
     { combo: 'Mod+Shift+Z', preventDefault: true, handler: () => redo() },
   ]);

   // Later
   hotkeys.disable();
   hotkeys.enable();
   hotkeys.dispose();

 Multiple combos for one action:
   createHotkeys([
     { combo: ['Ctrl+Slash', 'Ctrl+/'], handler: showHelp },
   ]);

 Using KeyboardEvent.code for layout-independent matching:
   createHotkeys([
     { combo: 'Ctrl+KeyK', handler: focusSearch }, // matches by e.code === 'KeyK'
   ]);

 Scopes:
   const hk = createHotkeys(
     [
       { combo: 'Escape', scope: 'modal', handler: closeModal },
       { combo: 'Delete', scope: ['editor', 'canvas'], handler: deleteSelected },
     ],
     { enabled: false, scopes: ['editor'] }
   );

   hk.enable();
   hk.addScope('modal'); // both 'editor' and 'modal' hotkeys are active
   hk.removeScope('editor');
   hk.setScopes(undefined); // no scopes → only definitions without scope can trigger

 Notes on combos:
 - Mod alias maps to Cmd on macOS/iOS and Ctrl on other platforms
 - You can use either event.key names (e.g., Enter, Escape, ArrowUp) or event.code names (KeyZ, Digit1)
 - When using a single character like 'z' or '+', it compares case-insensitively by key
 - strictModifiers option controls whether extra pressed modifiers should block the match
*/

/**
 * Scope label used to group hotkeys. When active scopes are set on the controller,
 * only definitions whose scope is included in the active set (or with no scope) can trigger.
 *
 * Example:
 *  - Definition: { combo: 'Escape', scope: 'modal' }
 *  - Controller scopes: ['modal'] → triggers; [] → does not trigger
 */
export type HotkeyScope = string;

/**
 * Describes a single hotkey rule.
 *
 * combo:
 *  - String or list of strings. Examples: 'Mod+Z', 'Ctrl+Shift+Z', 'Alt+Enter', 'Ctrl+KeyZ'.
 *  - Tokens are joined with '+'. Supported modifiers: Ctrl, Cmd/Meta, Shift, Alt, Mod.
 *  - Use event.code names (e.g., KeyZ, Digit1) for layout-independent matching.
 *  - Use event.key names (e.g., Enter, Escape, ArrowUp) for semantic keys.
 *
 * handler:
 *  - Function invoked when the combo matches. Receives the original KeyboardEvent.
 *
 * preventDefault / stopPropagation:
 *  - If true, calls e.preventDefault() / e.stopPropagation() when the combo matches.
 *
 * scope:
 *  - Restrict this hotkey to one or more scopes. Only active scopes match.
 *  - See HotkeysController.setScopes/addScope/removeScope.
 *
 * when:
 *  - Optional guard function. If provided and returns false, the handler will not run.
 */
export type HotkeyDefinition = {
  combo: string | string[];
  handler?: (e: KeyboardEvent) => void;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  scope?: HotkeyScope | HotkeyScope[];
  when?: () => boolean; // optional guard; when returns false, handler will not run
};

/**
 * Options to configure the behavior of createHotkeys.
 *
 * target:
 *  - Event target to attach the listener to. Defaults to window when available.
 *  - Can be Window | Document | HTMLElement.
 *
 * type:
 *  - Keyboard event type to listen to. One of 'keydown' | 'keyup' | 'keypress'.
 *  - Defaults to 'keydown'.
 *
 * enabled:
 *  - Whether the hotkeys listener is attached initially. Defaults to true.
 *
 * strictModifiers:
 *  - If true, the set of pressed modifiers must exactly match the combo.
 *    Example: Combo 'Ctrl+Z' will NOT match if user presses Ctrl+Shift+Z.
 *  - If false (default), extra pressed modifiers are tolerated as long as required ones are present.
 *
 * scopes:
 *  - Initial set of active scopes. Used with definition.scope to limit which hotkeys can trigger.
 */
export type CreateHotkeysOptions = {
  target?: Window | Document | HTMLElement;
  type?: 'keydown' | 'keyup' | 'keypress';
  enabled?: boolean;
  strictModifiers?: boolean; // require exact modifier set match
  scopes?: HotkeyScope[]; // active scopes
};

/**
 * Controller returned by createHotkeys to manage the listener and scopes.
 *
 * Methods:
 *  - enable(): Attach the listener if not attached.
 *  - disable(): Detach the listener without disposing.
 *  - dispose(): Fully cleans up: detaches and clears internal references.
 *  - isEnabled(): Returns whether the listener is currently attached.
 *  - setScopes(scopes): Replace the active scopes set (or undefined for no scopes).
 *  - addScope(scope): Add a scope to the active set.
 *  - removeScope(scope): Remove a scope from the active set.
 *  - update(defs): Replace hotkey definitions at runtime.
 */
export type HotkeysController = {
  enable: () => void;
  disable: () => void;
  dispose: () => void;
  isEnabled: () => boolean;
  setScopes: (scopes: HotkeyScope[] | undefined) => void;
  addScope: (scope: HotkeyScope) => void;
  removeScope: (scope: HotkeyScope) => void;
  update: (defs: HotkeyDefinition[]) => void;
};

const isMac = (() => {
  try {
    // @ts-ignore
    const nav = (typeof navigator !== 'undefined' && navigator) || null;
    return !!nav && /Mac|iPhone|iPad|iPod/i.test(nav.platform || '');
  } catch (e) {
    return false;
  }
})();

// Map common aliases to standardized keys
const KEY_ALIASES: Record<string, string> = {
  ESC: 'Escape',
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  RETURN: 'Enter',
  SPACE: ' ',
  SPACEBAR: ' ',
  TAB: 'Tab',
  BACKSPACE: 'Backspace',
  DELETE: 'Delete',
  DEL: 'Delete',
  INSERT: 'Insert',
  HOME: 'Home',
  END: 'End',
  PAGEUP: 'PageUp',
  PAGEDOWN: 'PageDown',
  UP: 'ArrowUp',
  DOWN: 'ArrowDown',
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
  PLUS: '+',
  MINUS: '-',
};

function normalizeKeyToken(tokenRaw: string): { mod?: 'ctrl' | 'meta' | 'shift' | 'alt' | 'mod'; key?: string; code?: string } {
  const token = tokenRaw.trim();
  const upper = token.toUpperCase();
  if (upper === 'CTRL' || upper === 'CONTROL' || upper === 'CTL') return { mod: 'ctrl' };
  if (upper === 'CMD' || upper === 'COMMAND' || upper === 'META') return { mod: 'meta' };
  if (upper === 'SHIFT') return { mod: 'shift' };
  if (upper === 'ALT' || upper === 'OPTION') return { mod: 'alt' };
  if (upper === 'MOD') return { mod: 'mod' };

  // Allow using code names like KeyZ, Digit1, etc.
  if (/^(Key|Digit|Numpad|Arrow|F\d{1,2})/.test(token)) {
    return { code: token };
  }

  // Letters/digits or named keys
  if (upper in KEY_ALIASES) {
    return { key: KEY_ALIASES[upper] };
  }
  if (token.length === 1) {
    return { key: token };
  }
  return { key: token };
}

function parseCombo(combo: string) {
  const parts = combo.split('+');
  const mods: { ctrl?: boolean; meta?: boolean; shift?: boolean; alt?: boolean; mod?: boolean } = {};
  let key: string | undefined;
  let code: string | undefined;
  for (const part of parts) {
    const { mod, key: k, code: c } = normalizeKeyToken(part);
    if (mod) {
      mods[mod] = true;
    } else if (c) {
      code = c;
    } else if (k) {
      key = k;
    }
  }
  // Default: if both key and code missing, this combo is modifiers-only
  return { mods, key, code } as const;
}

function eventMatchesCombo(e: KeyboardEvent, combo: ReturnType<typeof parseCombo>, strictModifiers: boolean) {
  const wantCtrl = !!combo.mods.ctrl || (!!combo.mods.mod && !isMac);
  const wantMeta = !!combo.mods.meta || (!!combo.mods.mod && isMac);
  const wantShift = !!combo.mods.shift;
  const wantAlt = !!combo.mods.alt;

  const hasCtrl = !!e.ctrlKey;
  const hasMeta = !!e.metaKey;
  const hasShift = !!e.shiftKey;
  const hasAlt = !!e.altKey;

  if (strictModifiers) {
    if (wantCtrl !== hasCtrl) return false;
    if (wantMeta !== hasMeta) return false;
    if (wantShift !== hasShift) return false;
    if (wantAlt !== hasAlt) return false;
  } else {
    if (wantCtrl && !hasCtrl) return false;
    if (wantMeta && !hasMeta) return false;
    if (wantShift && !hasShift) return false;
    if (wantAlt && !hasAlt) return false;
  }

  // If specific key or code is requested, check it
  if (combo.code) {
    if (e.code !== combo.code) return false;
  } else if (combo.key) {
    const ek = e.key?.length === 1 ? e.key : e.key; // keep as-is; aliases set by parse
    // Compare case-insensitively for single-letter keys
    if (combo.key.length === 1) {
      if (ek.toLowerCase() !== combo.key.toLowerCase()) return false;
    } else {
      if (ek !== combo.key) return false;
    }
  }

  return true;
}

function scopeMatches(defScopes: HotkeyScope[] | undefined, activeScopes: Set<HotkeyScope> | undefined) {
  if (!defScopes || defScopes.length === 0) return true;
  if (!activeScopes || activeScopes.size === 0) return false;
  for (const s of defScopes) if (activeScopes.has(s)) return true;
  return false;
}

/**
 * Create and attach keyboard shortcuts.
 *
 * Matching rules:
 * - Mod modifier resolves to Cmd on macOS/iOS and Ctrl on other platforms.
 * - If a combo specifies a code (e.g., KeyZ), it matches by event.code exactly.
 * - If a combo specifies a key (e.g., 'z' or 'Enter'), it matches by event.key.
 *   For single-letter keys, comparison is case-insensitive.
 * - strictModifiers controls whether extra pressed modifiers invalidate the match.
 * - Definitions are evaluated in order; the first matching definition handles the event.
 *
 * Scoping:
 * - A definition may provide scope or scopes; the controller may maintain an active set of scopes.
 * - A definition matches scopes when at least one of its scopes is present in the active set.
 * - If a definition has no scope set, it is always eligible to match.
 *
 * Error handling:
 * - Handler exceptions are caught and logged to console.error to avoid breaking global listeners.
 *
 * @param definitions List of hotkey definitions.
 * @param options Configuration for target, event type, initial enabled state, strict modifier mode, and scopes.
 * @returns HotkeysController for enabling/disabling, dispose, and scope/defs management.
 */
export function createHotkeys(definitions: HotkeyDefinition[], options?: CreateHotkeysOptions): HotkeysController {
  const target: any = options?.target || (typeof window !== 'undefined' ? window : undefined);
  const type = options?.type ?? 'keydown';
  const strictModifiers = options?.strictModifiers ?? false;
  let enabled = options?.enabled ?? true;

  let defs = definitions.slice();
  let activeScopes: Set<HotkeyScope> | undefined = options?.scopes ? new Set(options.scopes) : undefined;

  const parsed = new Map<string, ReturnType<typeof parseCombo>[]>();
  const combosCache = new WeakMap<HotkeyDefinition, ReturnType<typeof parseCombo>[]>();
  const normalizeDefs = () => {
    parsed.clear();
    for (const d of defs) {
      const combos = (Array.isArray(d.combo) ? d.combo : [d.combo]).map(parseCombo);
      combosCache.set(d, combos);
    }
  };

  normalizeDefs();

  const handler = (e: KeyboardEvent) => {
    if (!enabled) return;

    for (const d of defs) {
      const guardOk = typeof d.when === 'function' ? !!d.when() : true;
      if (!guardOk) continue;
      const defScopes = d.scope ? (Array.isArray(d.scope) ? d.scope : [d.scope]) : undefined;
      if (!scopeMatches(defScopes, activeScopes)) continue;

      const combos = combosCache.get(d)!;
      const matched = combos.some((c) => eventMatchesCombo(e, c, strictModifiers));
      if (matched) {
        if (d.preventDefault) e.preventDefault();
        if (d.stopPropagation) e.stopPropagation();
        try {
          d.handler?.(e);
        } catch (err) {
          // swallow errors to avoid breaking global listeners
          // eslint-disable-next-line no-console
          console.error('Hotkey handler error', err);
        }
        break; // stop on first match
      }
    }
  };

  const attach = () => {
    if (!target || !('addEventListener' in target)) return;
    target.addEventListener(type, handler as any);
  };
  const detach = () => {
    if (!target || !('removeEventListener' in target)) return;
    target.removeEventListener(type, handler as any);
  };

  if (enabled) attach();

  return {
    enable: () => {
      if (!enabled) {
        enabled = true;
        attach();
      }
    },
    disable: () => {
      if (enabled) {
        enabled = false;
        detach();
      }
    },
    dispose: () => {
      detach();
      defs = [];
      activeScopes = undefined;
    },
    isEnabled: () => enabled,
    setScopes: (scopes) => {
      activeScopes = scopes ? new Set(scopes) : undefined;
    },
    addScope: (scope) => {
      if (!activeScopes) activeScopes = new Set();
      activeScopes.add(scope);
    },
    removeScope: (scope) => {
      if (activeScopes) activeScopes.delete(scope);
    },
    update: (newDefs) => {
      defs = newDefs.slice();
      normalizeDefs();
    },
  };
}

export default createHotkeys;
